import { type FC, useEffect, useCallback } from 'react';
import { Flip, toast } from 'react-toastify';
import { ReleaseInfo } from '@/generated/ReleaseInfo';
import { IntercomUtils } from '@/utils/Intercom.utils';
import Button from '@/components/UI/Button/Button';
import { ReleaseChecker } from '@/utils/ReleaseChecker';
import { DatadogUtils } from '@/utils/Datadog.utils';
import styles from './UpdateChecker.module.scss';

interface UpdateAlertProps {
  onUpdate: () => void;
  onDefer: () => void;
}

const UpdateAlert: FC<UpdateAlertProps> = ({ onUpdate, onDefer }) => {
  return (
    <div>
      <div className="fw6 white lh-title f5 mb2">A better Benchling In Vivo is ready</div>
      <div className="f6 lh-copy mb3 light-gray normal">
        This update includes key improvements and bug fixes. Simply refresh your browser to apply it.
      </div>
      <div className="flex justify-between">
        <Button onClick={onUpdate} className="f6">
          Refresh and apply
        </Button>
        <Button onClick={onDefer} plain className="f6">
          Defer for 1 hour
        </Button>
      </div>
    </div>
  );
};

const deferUntilValue = (): number => {
  const deferUntil = Number(sessionStorage.getItem('update-alert-defer-until') ?? '0');
  return Number.isFinite(deferUntil) ? deferUntil : 0;
};

const toastId = 'update-toast';
export const UpdateChecker: FC = () => {
  const handleDefer = useCallback((updateVersion: string) => {
    toast.dismiss(toastId);
    const deferUntilUpdate = Date.now() + 1000 * 60 * 60; // Defer for 1 hour
    sessionStorage.setItem('update-alert-defer-until', `${deferUntilUpdate}`);
    IntercomUtils.trackEvent('user-deferred-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    DatadogUtils.captureAction('user-deferred-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
  }, []);

  const handleUpdate = useCallback((updateVersion: string) => {
    toast.dismiss(toastId);
    IntercomUtils.trackEvent('user-applied-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    DatadogUtils.captureAction('user-applied-update', {
      fromVersion: ReleaseInfo.releaseVersion,
      toVersion: updateVersion,
    });
    fetch(window.location.href, { cache: 'reload' })
      .then(() => window.location.reload())
      .catch(() => window.location.reload());
  }, []);

  const onUpdateAvailable = useCallback(
    ({ version }: { version: string }) => {
      if (deferUntilValue() <= Date.now() && !toast.isActive(toastId)) {
        IntercomUtils.trackEvent('update-alert-triggered', {
          fromVersion: ReleaseInfo.releaseVersion,
          toVersion: version,
        });
        DatadogUtils.captureAction('update-alert-triggered', {
          fromVersion: ReleaseInfo.releaseVersion,
          toVersion: version,
        });
        toast(<UpdateAlert onUpdate={() => handleUpdate(version)} onDefer={() => handleDefer(version)} />, {
          type: 'default',
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          toastId,
          position: 'top-right',
          className: styles.UpdateAlertToast,
          transition: Flip,
        });
      }
    },
    [handleDefer, handleUpdate]
  );

  useEffect(() => {
    const releaseChecker = new ReleaseChecker({
      onUpdateAvailable,
    }).initialise();
    return () => {
      releaseChecker.destroy();
    };
  }, [onUpdateAvailable]);

  return <></>;
};
